import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { constants } from '../constants';
import publication from '../../public/publication.svg';
import issuereport from '../../public/issuereport.svg';
import scheduled from '../../public/scheduled.svg';
import tellus from '../../public/tellus.svg';
import notificationicon from '../../public/Notificationicon.svg';
import inventoryIcon from '../../public/ConCON_Icons,inventory.svg';
import upgradeIcon from '../../public/upgrade.svg';
import maintenanceIcon from '../../public/Maintenance.svg';
export const NOTIFICATION_POLL_INTERVAL_MS = 30000;
// Notification title constants
export const NOTIFICATION_TITLES = {
    DATASET_PUBLISHED: 'Dataset published',
    ISSUE_REPORT: 'Issue report',
    SCHEDULED_OUTAGE: 'Scheduled outage',
    FEEDBACK: "Tell us how we're doing!",
    SURVEY_AVAILABLE: 'A survey is available for you to complete',
    UPGRADE_SCHEDULED: 'Upgrade scheduled',
    MAINTENANCE_SCHEDULED: 'Maintenance scheduled'
};
// Preload icons to prevent flickering - preload SVG images for CSS mask
const preloadIcon = (src) => {
    const img = new Image();
    img.src = src;
};
// Preload all icons immediately on module load
for (const icon of [
    publication,
    issuereport,
    scheduled,
    tellus,
    notificationicon,
    inventoryIcon,
    upgradeIcon,
    maintenanceIcon
]) {
    preloadIcon(icon);
}
/**
 * Returns the appropriate notification icon based on the title.
 */
export function getNotificationIcon(title, color) {
    // Determine which icon to use based on exact title matches
    let iconSrc = notificationicon;
    let iconTitle = constants.notification_title_default;
    const lowerTitle = title.toLowerCase().trim();
    if (lowerTitle.includes(NOTIFICATION_TITLES.DATASET_PUBLISHED.toLowerCase())) {
        iconSrc = publication;
        iconTitle = constants.notification_title_publication;
    }
    else if (lowerTitle.includes(NOTIFICATION_TITLES.ISSUE_REPORT.toLowerCase())) {
        iconSrc = issuereport;
        iconTitle = constants.notification_title_issue;
    }
    else if (lowerTitle.includes(NOTIFICATION_TITLES.SCHEDULED_OUTAGE.toLowerCase())) {
        iconSrc = scheduled;
        iconTitle = constants.notification_title_scheduled_outage;
    }
    else if (lowerTitle === NOTIFICATION_TITLES.FEEDBACK.toLowerCase()) {
        iconSrc = tellus;
        iconTitle = constants.notification_title_feedback;
    }
    else if (lowerTitle.includes(NOTIFICATION_TITLES.SURVEY_AVAILABLE.toLowerCase())) {
        iconSrc = inventoryIcon;
        iconTitle = constants.notification_title_survey;
    }
    else if (lowerTitle.includes(NOTIFICATION_TITLES.UPGRADE_SCHEDULED.toLowerCase())) {
        iconSrc = upgradeIcon;
        iconTitle = constants.notification_title_upgrade;
    }
    else if (lowerTitle.includes(NOTIFICATION_TITLES.MAINTENANCE_SCHEDULED.toLowerCase())) {
        iconSrc = maintenanceIcon;
        iconTitle = constants.notification_title_maintenance;
    }
    // If color is provided, use mask style with preloaded image
    if (color) {
        // Container with border
        const containerStyle = {
            width: '32px',
            height: '32px',
            border: '1px solid #D1D5DB', // Grey border like in the image
            borderRadius: '50%',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            position: 'relative'
        };
        // Mask style for the icon inside
        const maskStyle = {
            background: color,
            width: '20px', // Slightly smaller to fit inside the bordered container
            height: '20px',
            display: 'inline-block',
            WebkitMaskImage: `url(${iconSrc})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url(${iconSrc})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center'
        };
        return (_jsx("div", { style: containerStyle, title: iconTitle, children: _jsx("div", { style: maskStyle }) }));
    }
    // Without color, use img tag with border container
    return (_jsx("div", { style: {
            width: '32px',
            height: '32px',
            border: '1px solid #D1D5DB', // Grey border like in the image
            borderRadius: '50%',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent'
        }, title: iconTitle, children: _jsx("img", { src: iconSrc, alt: iconTitle, loading: "eager", style: {
                width: '20px', // Slightly smaller to fit inside the bordered container
                height: '20px',
                display: 'block'
            } }) }));
}
/**
 * Helper function to sort notifications by time (most recent first)
 */
function sortByTimeDescending(notifications) {
    return notifications.sort((a, b) => {
        const aTime = a.meta?.created ? new Date(a.meta.created).getTime() : 0;
        const bTime = b.meta?.created ? new Date(b.meta.created).getTime() : 0;
        return bTime - aTime;
    });
}
/**
 * Groups notifications into "recent" (last 7 days, no heading), "lastWeek", "lastMonth", and "older".
 * Feedback notifications ("Tell us how we're doing!") are always placed at the top within each group.
 */
export function groupNotifications(notifications) {
    const now = new Date();
    const recent = [];
    const lastWeek = [];
    const lastMonth = [];
    const older = [];
    const startOfRecent = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const startOfLastWeek = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    for (const n of notifications) {
        const created = n.meta?.created ? new Date(n.meta.created) : null;
        if (!created)
            continue;
        if (created >= startOfRecent) {
            recent.push(n);
        }
        else if (created >= startOfLastWeek) {
            lastWeek.push(n);
        }
        else if (created >= startOfLastMonth) {
            lastMonth.push(n);
        }
        else {
            older.push(n);
        }
    }
    // Return groups sorted by time (most recent first) - feedback prioritization is handled in the component
    return {
        recent: sortByTimeDescending(recent),
        lastWeek: sortByTimeDescending(lastWeek),
        lastMonth: sortByTimeDescending(lastMonth),
        older: sortByTimeDescending(older)
    };
}
/**
 * Returns a "time ago" string for a given date.
 * For dates older than 7 days, returns formatted date (e.g., "Oct 31").
 */
export function getTimeAgo(dateString) {
    if (!dateString)
        return '';
    const now = new Date();
    const date = new Date(dateString);
    const diff = (now.getTime() - date.getTime()) / 1000;
    const diffInDays = diff / 86400;
    if (diff < 60)
        return `${Math.floor(diff)}s ${constants.time_ago_suffix}`;
    if (diff < 3600)
        return `${Math.floor(diff / 60)}min ${constants.time_ago_suffix}`;
    if (diff < 86400)
        return `${Math.floor(diff / 3600)}h ${constants.time_ago_suffix}`;
    if (diffInDays < 7)
        return `${Math.floor(diffInDays)}d ${constants.time_ago_suffix}`;
    // For dates older than 7 days, show formatted date (e.g., "Oct 31")
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
/**
 * Formats a date/time according to user's browser locale.
 * Returns format like "11/06/2025 11:00AM"
 */
export function formatScheduledDateTime(dateString) {
    if (!dateString)
        return '';
    const date = new Date(dateString);
    // Get user's browser locale
    const locale = navigator.language || 'en-US';
    // Format date and time together using toLocaleString
    return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
}
/**
 * Gets the formatted message for upgrade/maintenance notifications.
 * If the notification has scheduled and expires fields, formats the message
 * with the date range according to user's browser locale.
 */
export function getFormattedNotificationMessage(title, message, scheduled, expires) {
    const lowerTitle = title.toLowerCase().trim();
    // Check if this is an upgrade or maintenance notification
    const isUpgradeOrMaintenance = lowerTitle.includes(NOTIFICATION_TITLES.UPGRADE_SCHEDULED.toLowerCase()) ||
        lowerTitle.includes(NOTIFICATION_TITLES.MAINTENANCE_SCHEDULED.toLowerCase());
    // If it's upgrade/maintenance and has scheduled/expires dates, replace placeholders in the API message
    if (isUpgradeOrMaintenance && scheduled && expires) {
        const formattedScheduled = formatScheduledDateTime(scheduled);
        const formattedExpires = formatScheduledDateTime(expires);
        const formattedMessage = message
            .replace('<scheduled>', formattedScheduled)
            .replace('<expires>', formattedExpires);
        return formattedMessage;
    }
    return message;
}
